/**
 * Parse user-entered JSON tolerantly.
 *
 * Browsers' JSON.parse rejects several invisible characters that are routinely
 * introduced when JSON is copied from a rendered source (chat, docs, web pages):
 *   - U+00A0 non-breaking space used as indentation (not valid JSON whitespace)
 *   - zero-width characters / BOM
 *   - raw ASCII control characters (e.g. a stray newline pasted inside a string)
 *
 * None of these can appear in well-formed JSON, so removing/normalizing them
 * before parsing never changes a legitimate value: real whitespace is optional
 * between tokens, and intentional newlines inside strings are encoded as the
 * two-character escape "\\n", which is left untouched.
 *
 * Patterns are built with String.fromCharCode so the source contains no literal
 * invisible characters (which are error-prone to maintain and trip no-control-regex).
 */
const NBSP = new RegExp(String.fromCharCode(0xa0), 'g')
const ZERO_WIDTH = new RegExp(
  '[' +
    String.fromCharCode(0x200b) +
    '-' +
    String.fromCharCode(0x200d) +
    String.fromCharCode(0xfeff) +
    ']',
  'g',
)
const CONTROL_CHARS = new RegExp(
  '[' + String.fromCharCode(0x00) + '-' + String.fromCharCode(0x1f) + ']',
  'g',
)

export function sanitizeJsonText(raw: string): string {
  return raw.replace(NBSP, ' ').replace(ZERO_WIDTH, '').replace(CONTROL_CHARS, '').trim()
}

export function parseLenientJson<T = unknown>(raw: string): T {
  return JSON.parse(sanitizeJsonText(raw)) as T
}
